import { PlusOutlined } from '@ant-design/icons';
import { Form, Layout } from 'antd';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { SDetailLayout } from 'app/components/StaycationUI';
import { AssetDetailTitle } from 'app/components/commons/AssetDetailTitle/AssetDetailTitle';
import { Desk } from 'app/components/commons/Hotel/Spaces/Desk/Desk';
import { Space } from 'app/components/commons/Hotel/Spaces/Space/Space';
import { SpaceModal } from 'app/components/commons/Hotel/Spaces/SpaceModal/SpaceModal';
import type { SpacesFormData } from 'app/components/commons/Hotel/Spaces/utils';
import {
  formatNewSpaces,
  formatSpacesFormFromAPI,
  reformatSpacesFormForAPI,
} from 'app/components/commons/Hotel/Spaces/utils';
import type { SpaceSubmitType } from 'app/components/commons/Hotel/Types';
import { FormLegacyRenderProp } from 'app/components/forms/FormLegacyRenderProp';
import { useGetExperiencesByHotelId } from 'app/hooks/data/core/admin/hotels/useHotelExperiences';
import { useSaveHotelSpaces } from 'app/hooks/data/hotels/useHotelSpaces';
import { useExperiencesCategories } from 'app/hooks/data/useExperience';
import { useHotelSpacesTypes } from 'app/hooks/data/useHotelSpacesTypes';
import type { HotelInfo } from 'app/typings/hotels/Hotel/Hotel';
import { SCROLL_TO_FIRST_ERROR } from 'app/utils/scroll';

import { SpaceItem } from './SpaceItem/SpaceItem';
import { SpaceLinkedExperiences } from './SpaceLinkedExperiences/SpaceLinkedExperiences';
import './Spaces.scss';

const { Content } = Layout;

type SpacesProps = {
  headerMenu: ReactNode;
  headerExtra?: ReactNode[];
  hotel?: HotelInfo;
  reloadHotel: () => void;
};

export const Spaces = ({
  headerMenu,
  headerExtra,
  hotel,
  reloadHotel,
}: SpacesProps) => {
  const { data: spacesTypes } = useHotelSpacesTypes();
  const { data: experiencesData } = useGetExperiencesByHotelId(hotel?.id);
  const { data: experienceCategories } = useExperiencesCategories();
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();

  const { mutateAsync: saveSpaces } = useSaveHotelSpaces(`${hotel?.id}`);

  const categoryNameById = useMemo(
    () =>
      (experienceCategories ?? []).reduce<Record<number, string>>(
        (acc, category) => {
          acc[category.id] = category.name;

          return acc;
        },
        {}
      ),
    [experienceCategories]
  );

  const onFinish = useCallback(
    async (values: SpacesFormData) => {
      const reformatted = reformatSpacesFormForAPI(
        values,
        hotel,
        spacesTypes || []
      );

      await saveSpaces(reformatted as unknown as SpaceSubmitType);
      await reloadHotel();
      setMode('view');
    },
    [hotel, reloadHotel, saveSpaces, spacesTypes]
  );

  const onCancel = useCallback(() => {
    setMode('view');
  }, []);

  const formattedValues = useMemo(() => {
    if (!hotel) {
      return null;
    }

    return formatSpacesFormFromAPI(hotel, spacesTypes || []);
  }, [hotel, spacesTypes]);

  const onModalSave = (newSpaces: number[]) => {
    const values = form.getFieldsValue();
    const formatted = formatNewSpaces(
      newSpaces,
      values,
      spacesTypes || [],
      true
    );

    form.setFieldsValue(formatted);
  };

  useEffect(() => {
    form.setFieldsValue(formattedValues);
  }, [form, formattedValues, hotel]);

  if (!hotel) {
    return null;
  }

  return (
    <FormLegacyRenderProp
      form={form}
      onFinish={onFinish}
      initialValues={formattedValues || {}}
      className="spaces-form-master"
      scrollToFirstError={SCROLL_TO_FIRST_ERROR}
    >
      {(
        values,
        { resetFields, isFieldsTouched, submit, setFieldsValue, validateFields }
      ) => (
        <SDetailLayout
          title={
            <AssetDetailTitle
              assetTitle={hotel?.name ?? `Hôtel #${hotel?.id}`}
              hotelName={hotel?.name}
              isNewDataModel={!hotel?.isLegacy}
            />
          }
          backRoute="/hotels"
          mode={mode}
          onEdit={() => setMode('edit')}
          onSave={submit}
          onCancel={onCancel}
          reset={resetFields}
          isDirty={isFieldsTouched()}
          headerMenu={headerMenu}
          headerExtra={headerExtra}
          isValid={form
            .getFieldsError()
            .every((item) => item.errors.length > 0)}
        >
          <Content className="hotel-detail__content">
            <SpaceItem title="Desk" expanded={!!hotel.spaces}>
              <Desk mode={mode} values={values} />
            </SpaceItem>
            <Form.List name="spaces">
              {(fields, { remove }) => (
                <>
                  {fields.map((field, idx) => {
                    const space = values.spaces[idx];
                    const spaceType = spacesTypes?.find(
                      (type) => type.id === space.spaceTypeId
                    );

                    if (!space || !spaceType) {
                      return null;
                    }

                    return (
                      <SpaceItem
                        title={spaceType.name}
                        onRemove={
                          mode === 'edit' ? () => remove(idx) : undefined
                        }
                        expanded={space?.addedSpace}
                        key={`${spaceType.id}-${idx}`}
                      >
                        <Space
                          mode={mode}
                          values={values}
                          field={field}
                          fieldIndex={idx}
                          spaceType={spaceType}
                          setFieldsValue={setFieldsValue}
                          hotelId={hotel.id}
                          validateFields={validateFields}
                          previousPrefixes={['spaces']}
                        />
                        {space.id !== undefined && (
                          <SpaceLinkedExperiences
                            spaceId={space.id}
                            experiences={experiencesData?.experiences ?? []}
                            categoryNameById={categoryNameById}
                          />
                        )}
                      </SpaceItem>
                    );
                  })}
                  {mode === 'edit' && (
                    <div className="spaces__add-button-container">
                      <div
                        onClick={() => setIsModalOpen(true)}
                        className="spaces__add-button"
                      >
                        <PlusOutlined />
                        <span className="text">Add spaces</span>
                      </div>
                      <SpaceModal
                        onCancel={() => setIsModalOpen(false)}
                        onSave={onModalSave}
                        visible={isModalOpen}
                        values={values}
                        spaceTypes={spacesTypes || []}
                      />
                    </div>
                  )}
                </>
              )}
            </Form.List>
          </Content>
        </SDetailLayout>
      )}
    </FormLegacyRenderProp>
  );
};
